/**
 * eNEW Studio プライバシーポリシーデータ
 * 個人情報保護法に準拠した包括的なプライバシーポリシー
 */

import type { PrivacyPolicyData } from '../types/privacy-policy';

export const privacyPolicyData: PrivacyPolicyData = {
  lastUpdated: '2026-01-23',
  establishedDate: '2026-01-23',
  sections: [
    {
      id: 'business-info',
      title: '事業者情報',
      content: [
        '事業者名: 株式会社eNEW Studio',
        '所在地: 〒920-0849 石川県金沢市堀川新町５番１号 アリマビル３F',
        '代表者: 伊原健太',
        'お問い合わせ: contact@enew-studio.co.jp'
      ]
    },
    {
      id: 'collection-purpose',
      title: '個人情報の収集について',
      content: '当社は、以下の目的で個人情報を収集します：',
      subsections: [
        {
          id: 'purpose-list',
          title: '収集目的',
          content: [
            'お問い合わせへの対応',
            'サービスに関する情報提供',
            '統計データの作成（個人を特定できない形式）'
          ]
        }
      ]
    },
    {
      id: 'personal-info-types',
      title: '収集する個人情報の種類',
      content: 'お問い合わせフォームを通じて以下の情報を収集いたします：',
      subsections: [
        {
          id: 'contact-form-info',
          title: 'お問い合わせフォーム情報',
          content: [
            '氏名',
            'メールアドレス',
            '会社名・団体名',
            '電話番号',
            'お問い合わせ内容'
          ]
        }
      ]
    },
    {
      id: 'usage-purpose',
      title: '個人情報の利用目的',
      content: '収集した個人情報は以下の目的で利用いたします：',
      subsections: [
        {
          id: 'usage-list',
          title: '利用目的一覧',
          content: [
            'お問い合わせへの回答・対応',
            'サービス・製品に関する情報提供',
            'お客様との連絡・コミュニケーション',
            'サービス改善のための統計分析（個人を特定しない形式）'
          ]
        }
      ]
    },
    {
      id: 'third-party-provision',
      title: '第三者提供について',
      content: '当社は、以下の場合を除き、個人情報を第三者に提供いたしません：',
      subsections: [
        {
          id: 'provision-exceptions',
          title: '第三者提供の例外',
          content: [
            'ご本人の同意がある場合',
            '法令に基づく場合',
            '人の生命、身体または財産の保護のために必要がある場合'
          ]
        },
        {
          id: 'service-providers',
          title: '利用するサービス',
          content: [
            'Slack（内部コミュニケーション用通知）',
            'Amazon Web Services（データ処理・保存）'
          ]
        }
      ]
    },
    {
      id: 'disclosure-correction-deletion',
      title: '開示・訂正・削除について',
      content: 'ご本人からの個人情報の開示、訂正、削除のご請求については、以下の連絡先までお問い合わせください：',
      subsections: [
        {
          id: 'request-procedure',
          title: '請求手続き',
          content: [
            'メール: contact@enew-studio.co.jp',
            '件名に「個人情報に関するお問い合わせ」と記載',
            'ご本人確認のため、必要な情報をお伺いする場合があります',
            '合理的な期間内に対応いたします'
          ]
        }
      ]
    },
    {
      id: 'cookies-analytics',
      title: 'Cookie・解析ツールについて',
      content: '当サイトでは、サービス向上のため以下のツールを使用する場合があります：',
      subsections: [
        {
          id: 'analytics-tools',
          title: '解析ツール',
          content: [
            'Google Analytics（ウェブサイト利用状況の分析）',
            'Cookie（サイト利用状況の改善）',
            'これらのツールは匿名化された情報を収集します',
            'ブラウザの設定でCookieを無効にすることができます'
          ]
        }
      ]
    },
    {
      id: 'contact-info',
      title: 'お問い合わせ窓口',
      content: 'プライバシーポリシーに関するお問い合わせは、以下までご連絡ください：',
      subsections: [
        {
          id: 'contact-details',
          title: '連絡先情報',
          content: [
            '株式会社eNEW Studio',
            '〒920-0849 石川県金沢市堀川新町５番１号 アリマビル３F',
            'メール: contact@enew-studio.co.jp',
            '受付時間: 平日 9:00-18:00'
          ]
        }
      ]
    },
    {
      id: 'data-retention',
      title: 'データ保持期間',
      content: '個人情報の保持期間は以下の通りです：',
      subsections: [
        {
          id: 'retention-policy',
          title: '保持期間・削除ポリシー',
          content: [
            'お問い合わせ情報: 対応完了後3年間',
            '統計データ: 個人を特定できない形式で保持',
            '保持期間経過後は適切に削除いたします',
            'お客様からの削除要請があった場合は速やかに対応いたします'
          ]
        }
      ]
    },
    {
      id: 'data-location',
      title: 'データの保管場所',
      content: 'お客様の個人情報は、日本国内のサーバーにて保管いたします。当社が利用するAmazon Web Servicesのサービスについても、東京リージョン（ap-northeast-1）を使用し、データが日本国外に転送されることはありません。'
    },
    {
      id: 'policy-changes',
      title: 'プライバシーポリシーの変更',
      content: '当社は、法令の改正やサービス内容の変更等に伴い、本プライバシーポリシーを変更することがあります。変更後のプライバシーポリシーは、本ページに掲載した時点から効力を生じるものとします。重要な変更がある場合は、本ページにてお知らせいたします。'
    }
  ]
};